import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../../../hooks/useAppStore';
import { PageLayout } from '../shared/PageLayout';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../shared/SlideOver';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../shared/StatCard';
import { MasteryBar } from '../shared/MasteryBar';
import { Avatar } from '../shared/Avatar';
import { SearchIcon, SchoolIcon, UsersIcon, TrendingUpIcon, GraduationCapIcon, Plus } from 'lucide-react';
import { Button } from '../../../ui/Button';
interface SchoolData {
  name: string;
  learnerCount: number;
  teacherCount: number;
  avgMastery: number;
  learners: Array<{
    id: string;
    displayName: string;
    groupName?: string;
    avgMastery: number;
    sessionCount: number;
  }>;
  teachers: Array<{
    id: string;
    displayName: string;
  }>;
}
export function SchoolsPage() {
  const {
    accounts,
    createAccount,
    getLearnerData
  } = useAppStore();
  // accounts is AccountsState (object) — extract flat array
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  // sessions and masteryRecords are not available in useAppStore — use safe empty arrays
  const sessions: any[] = [];
  const masteryRecords: any[] = [];
  const [search, setSearch] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<string | null>(null);
  // Add School Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');
  const [newTeacherName, setNewTeacherName] = useState('');
  const handleAddSchool = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSchoolName.trim() || !newTeacherName.trim()) return;
    createAccount('teacher', {
      displayName: newTeacherName,
      schoolName: newSchoolName
    });
    setIsAddModalOpen(false);
    setNewSchoolName('');
    setNewTeacherName('');
  };
  const schoolsData = useMemo((): SchoolData[] => {
    const schoolNames = Array.from(new Set(allAccounts.map(a => (a as any).schoolName).filter(Boolean) as string[])).sort();
    return schoolNames.map(schoolName => {
      const schoolLearners = allAccounts.filter(a => a.role === 'learner' && (a as any).schoolName === schoolName);
      const schoolTeachers = allAccounts.filter(a => a.role === 'teacher' && (a as any).schoolName === schoolName);
      const enrichedLearners = schoolLearners.map(learner => {
        const learnerData = getLearnerData(learner.id);
        const familyMastery = learnerData?.familyMastery || {};
        const masteryValues = Object.values(familyMastery).map(m => m.masteryScore);
        const avgMastery = masteryValues.length > 0 ? Math.round(masteryValues.reduce((sum, val) => sum + val, 0) / masteryValues.length) : 0;
        const totalAttempts = Object.values(familyMastery).reduce((sum, m) => sum + m.attempts, 0);
        const sessionCount = Math.ceil(totalAttempts / 10);
        return {
          id: learner.id,
          displayName: learner.displayName,
          groupName: (learner as any).groupName,
          avgMastery,
          sessionCount
        };
      });
      const avgMastery = enrichedLearners.length > 0 ? Math.round(enrichedLearners.reduce((sum, l) => sum + l.avgMastery, 0) / enrichedLearners.length) : 0;
      return {
        name: schoolName,
        learnerCount: schoolLearners.length,
        teacherCount: schoolTeachers.length,
        avgMastery,
        learners: enrichedLearners,
        teachers: schoolTeachers.map(t => ({
          id: t.id,
          displayName: t.displayName
        }))
      };
    });
  }, [allAccounts, getLearnerData]);
  const filtered = useMemo(() => {
    return schoolsData.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()));
  }, [schoolsData, search]);
  const selectedSchoolData = useMemo(() => selectedSchool ? schoolsData.find(s => s.name === selectedSchool) : null, [selectedSchool, schoolsData]);
  const totalLearners = useMemo(() => schoolsData.reduce((sum, s) => sum + s.learnerCount, 0), [schoolsData]);
  const avgMasteryAll = useMemo(() => {
    if (schoolsData.length === 0) return 0;
    return Math.round(schoolsData.reduce((sum, s) => sum + s.avgMastery, 0) / schoolsData.length);
  }, [schoolsData]);
  const columns = [{
    key: 'name',
    label: 'School',
    render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-885">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-886">
            <SchoolIcon className="w-4 h-4 text-purple-600" data-id="element-887" />
          </div>
          <span className="text-sm font-medium text-gray-900" data-id="element-888">{v}</span>
        </div>
  }, {
    key: 'learnerCount',
    label: 'Leerlingen',
    render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-889">
          <UsersIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-890" />
          {v}
        </span>
  }, {
    key: 'teacherCount',
    label: 'Docenten',
    render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-891">
          <GraduationCapIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-892" />
          {v}
        </span>
  }, {
    key: 'avgMastery',
    label: 'Gem. beheersing',
    render: (v: number) => <MasteryBar value={v} data-id="element-893" />
  }];
  return <PageLayout title="Scholen" description={`${filtered.length} scholen`} actions={<div className="flex gap-2" data-id="element-895">
          <ConnectionBadge status="live" data-id="element-896" />
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-897">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-898" />
            School toevoegen
          </Button>
        </div>} data-id="element-894">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-899">
        <StatCard label="Scholen" value={schoolsData.length} icon={<SchoolIcon className="w-5 h-5" data-id="element-901" />} color="purple" data-id="element-900" />
        <StatCard label="Leerlingen totaal" value={totalLearners} icon={<UsersIcon className="w-5 h-5" data-id="element-903" />} color="blue" data-id="element-902" />
        <StatCard label="Docenten totaal" value={allAccounts.filter(a => a.role === 'teacher').length} icon={<GraduationCapIcon className="w-5 h-5" data-id="element-905" />} color="indigo" data-id="element-904" />
        <StatCard label="Gem. beheersing" value={`${avgMasteryAll}%`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-907" />} color="emerald" data-id="element-906" />
      </div>

      <div className="relative mb-4" data-id="element-908">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-909" />
        <input type="text" placeholder="Zoek op schoolnaam..." value={search} onChange={e => setSearch(e.target.value)} className="w-full max-w-sm pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-910" />
      </div>

      {filtered.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center" data-id="element-911">
          <SchoolIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-912" />
          <p className="text-sm text-gray-500" data-id="element-913">Geen scholen gevonden</p>
        </div> : <DataTable columns={columns} data={filtered} onRowClick={row => setSelectedSchool(row.name)} data-id="element-914" />}

      <SlideOver open={!!selectedSchoolData} onClose={() => setSelectedSchool(null)} title={selectedSchoolData?.name ?? ''} data-id="element-915">
        {selectedSchoolData && <div className="space-y-6" data-id="element-916">
            <div className="grid grid-cols-3 gap-3" data-id="element-917">
              <div className="bg-purple-50 rounded-lg p-3 text-center" data-id="element-918">
                <div className="text-2xl font-bold text-purple-700" data-id="element-919">
                  {selectedSchoolData.learnerCount}
                </div>
                <div className="text-xs text-purple-600" data-id="element-920">Leerlingen</div>
              </div>
              <div className="bg-indigo-50 rounded-lg p-3 text-center" data-id="element-921">
                <div className="text-2xl font-bold text-indigo-700" data-id="element-922">
                  {selectedSchoolData.teacherCount}
                </div>
                <div className="text-xs text-indigo-600" data-id="element-923">Docenten</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center" data-id="element-924">
                <div className="text-2xl font-bold text-emerald-700" data-id="element-925">
                  {selectedSchoolData.avgMastery}%
                </div>
                <div className="text-xs text-emerald-600" data-id="element-926">Gem. beheersing</div>
              </div>
            </div>

            {selectedSchoolData.teachers.length > 0 && <div data-id="element-927">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-928">
                  Docenten
                </h3>
                <div className="space-y-1" data-id="element-929">
                  {selectedSchoolData.teachers.map(t => <div key={t.id} className="flex items-center gap-2 py-1.5" data-id="element-930">
                      <Avatar name={t.displayName} data-id="element-931" />
                      <span className="text-sm text-gray-700" data-id="element-932">
                        {t.displayName}
                      </span>
                    </div>)}
                </div>
              </div>}

            {selectedSchoolData.learners.length > 0 && <div data-id="element-933">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-934">
                  Leerlingen
                </h3>
                <div className="space-y-2" data-id="element-935">
                  {selectedSchoolData.learners.map(learner => <div key={learner.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0" data-id="element-936">
                      <div className="flex items-center gap-2" data-id="element-937">
                        <Avatar name={learner.displayName} data-id="element-938" />
                        <div data-id="element-939">
                          <div className="text-sm text-gray-800" data-id="element-940">
                            {learner.displayName}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-941">
                            {learner.groupName ?? '—'} · {learner.sessionCount}{' '}
                            sessies
                          </div>
                        </div>
                      </div>
                      <MasteryBar value={learner.avgMastery} data-id="element-942" />
                    </div>)}
                </div>
              </div>}
          </div>}
      </SlideOver>

      {/* Add School Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe school" description="Voeg een school toe door de eerste docent aan te maken" data-id="element-943">
        <form onSubmit={handleAddSchool} className="space-y-4" data-id="element-944">
          <div data-id="element-945">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-946">
              Schoolnaam
            </label>
            <input type="text" required value={newSchoolName} onChange={e => setNewSchoolName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Bijv. De Regenboog" data-id="element-947" />
          </div>

          <div data-id="element-948">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-949">
              Naam eerste docent
            </label>
            <input type="text" required value={newTeacherName} onChange={e => setNewTeacherName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Bijv. Meester Jan" data-id="element-950" />
          </div>

          <div className="pt-4 flex justify-end gap-2" data-id="element-951">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)} data-id="element-952">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-953">Aanmaken</Button>
          </div>
        </form>
      </SlideOver>
    </PageLayout>;
}